import { useRetryGapAnalysis } from "@/lib/api/assessments";
import { Loader2Icon, RefreshCwIcon } from "lucide-react";

// Rendered when a completed blind attempt has no gap_analysis row yet
// (LLM call previously failed, or never ran). The user clicks "Generate
// report" to trigger POST /attempts/:id/retry-gap-analysis. On success, the
// hook invalidates the attempt query and the parent re-renders the report.
//
// We deliberately don't auto-retry on first load — every reload would burn
// an LLM call. The button gives the user a deliberate, single action.
export function GapAnalysisRetryPanel({ attemptId }: { attemptId: number }) {
  const retry = useRetryGapAnalysis();

  return (
    <article className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10">
      <div
        className="flex items-center gap-3"
        style={{ color: "var(--muted-foreground)", fontSize: "13px" }}
      >
        <span>Diagnostic complete</span>
      </div>

      <h1
        className="mt-10 font-medium leading-tight tracking-tight"
        style={{
          fontFamily: "var(--font-serif, ui-serif, Georgia, serif)",
          fontSize: "26px",
        }}
      >
        Your report is being prepared.
      </h1>

      <p
        className="mt-3 leading-relaxed max-w-2xl"
        style={{ color: "var(--muted-foreground)", fontSize: "15.5px" }}
      >
        We didn't generate it the first time. Click below to try again — it
        takes about ten seconds.
      </p>

      {retry.isError && (
        <p
          className="mt-4 leading-relaxed"
          style={{ color: "var(--destructive)", fontSize: "14px" }}
        >
          {retry.error?.message ||
            "Something went wrong. Try again in a moment."}
        </p>
      )}

      <div className="mt-8">
        <button
          type="button"
          onClick={() => retry.mutate(attemptId)}
          disabled={retry.isPending}
          className="inline-flex items-center justify-center rounded-xl font-semibold transition hover:opacity-90 active:translate-y-px disabled:opacity-60 disabled:cursor-not-allowed"
          style={{
            background: "var(--foreground)",
            color: "var(--background)",
            boxShadow: "0 1px 3px rgba(0,0,0,0.10)",
            padding: "14px 24px",
            fontSize: "15px",
          }}
        >
          {retry.isPending ? (
            <>
              <Loader2Icon className="mr-2 size-4 animate-spin" />
              Generating
            </>
          ) : (
            <>
              <RefreshCwIcon className="mr-2 size-4" />
              Generate report
            </>
          )}
        </button>
      </div>
    </article>
  );
}
